import { useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { Controller } from 'react-hook-form';
import { useFuelAutocompleteProps } from '@deere/fuel-react/useFuelAutocompleteProps.js';
import { searchSuppliersByQuery } from '../shared/APIHelper';

const MIN_SEARCH_CHARS = 3;

export default function SupplierDropDownSearch(props) {
  const {
    name,
    control,
    readOnly,
    defaultValue,
    setStatusErrorMsg,
    showFilter,
    handleInputChange,
    suppliers,
    setSelectedSuppliers = () => {},
    showSingleOption,
    addSupplierError,
    loadOptionsOverride,
    placeholderText,
    defaultOptions,
    controlShouldRenderValue = true
  } = props;

  const isMulti = !showSingleOption;
  const emptyValue = isMulti ? [] : null;
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState(
    Array.isArray(defaultOptions) ? defaultOptions : []
  );

  const fuelAutocompleteProps = useFuelAutocompleteProps(
    isMulti
      ? {
          multiple: true,
          slotProps: {
            displayText: {
              renderText: (values) =>
                values.length === 1
                  ? values[0].label
                  : `${values.length} suppliers selected`
            }
          }
        }
      : { multiple: false }
  );

  const getOptionLabel = useMemo(
    () => (option) => {
      if (typeof option === 'string') return option;
      return option?.label || '';
    },
    []
  );

  const isOptionEqualToValue = useMemo(
    () => (option, value) => option?.value === value?.value,
    []
  );

  const resolvedPlaceholder =
    placeholderText ||
    (showFilter
      ? 'Search by Name or Number (3+ chars)'
      : 'Search (enter 3 or more characters)');

  const filterOptions = (suppliersList, inputValue) => {
    const query = (inputValue || '').toLowerCase();

    return suppliersList.filter((option) =>
      (option?.label || '').toLowerCase().includes(query)
    );
  };

  const mapApiResultsToOptions = (results = []) =>
    results.map((item) => ({
      value: item.supplierNumber,
      label: `${item.supplierNumber} - ${item.supplierName}`,
      city: item.city ?? '',
      region: item.state ?? '',
      country: item.country ?? ''
    }));

  const loadOptions = (inputValue, callback) => {
    if ((inputValue || '').length < MIN_SEARCH_CHARS) {
      const defaults = Array.isArray(defaultOptions) ? defaultOptions : [];
      setOptions(defaults);
      callback?.(defaults);
      return;
    }

    setIsLoading(true);
    setStatusErrorMsg?.(null);

    if (loadOptionsOverride) {
      try {
        loadOptionsOverride(inputValue, (items) => {
          const nextOptions = Array.isArray(items) ? items : [];
          setOptions(nextOptions);
          callback?.(nextOptions);
          setIsLoading(false);
        });
      } catch (error) {
        setStatusErrorMsg?.("Couldn't fetch Suppliers.", error);
        setOptions([]);
        callback?.([]);
        setIsLoading(false);
      }

      return;
    }

    searchSuppliersByQuery(inputValue)
      .then((results) => {
        const nextOptions = filterOptions(
          mapApiResultsToOptions(results),
          inputValue
        );
        setOptions(nextOptions);
        callback?.(nextOptions);
        setIsLoading(false);
      })
      .catch((error) => {
        setStatusErrorMsg?.("Couldn't fetch Suppliers.", error);
        setOptions([]);
        callback?.([]);
        setIsLoading(false);
      });
  };

  const keydownHandler = (event) => {
    event.stopPropagation();
  };

  const handleTextInput = (_event, nextInputValue, reason) => {
    if (reason === 'input') {
      setSelectedSuppliers(nextInputValue);
      loadOptions(nextInputValue);
    }

    if (reason === 'clear') {
      const defaults = Array.isArray(defaultOptions) ? defaultOptions : [];
      setOptions(defaults);
    }
  };

  const renderAutocomplete = ({ value, onChange }) => (
    <Autocomplete
      {...fuelAutocompleteProps}
      fullWidth
      aria-label={resolvedPlaceholder}
      options={options}
      loading={isLoading}
      disabled={readOnly}
      multiple={isMulti}
      value={value}
      disableCloseOnSelect={isMulti}
      openOnFocus={Boolean(showFilter)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      onKeyDown={keydownHandler}
      onInputChange={handleTextInput}
      onChange={(_event, nextValue) => {
        onChange(nextValue);
      }}
      renderTags={!controlShouldRenderValue && isMulti ? () => null : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          error={Boolean(addSupplierError)}
          placeholder={resolvedPlaceholder}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  );

  if (showFilter) {
    return renderAutocomplete({
      value: suppliers || emptyValue,
      onChange: (nextValue) => handleInputChange?.(nextValue)
    });
  }

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || emptyValue}
      render={({ field }) =>
        renderAutocomplete({
          value: field.value || emptyValue,
          onChange: field.onChange
        })
      }
    />
  );
}
